import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { MessagingService } from '@app/shared/services/messaging/messaging.service';
import { MessagingApiService } from '@app/shared/services/messaging/messaging-api.service';
import {
  ConversationMessage,
  ConversationPerson,
  ConversationSummary,
  documentViewUrl,
} from '@app/shared/services/messaging/messaging.models';

/**
 * User-to-user messaging in the shell (ZEY-2259): a right-side panel opened from the Messages
 * icon beside the bell, or from a record's Discuss button. Three views — the conversation list,
 * one conversation, and starting a new one. Sits beside the help drawer and follows its layout.
 *
 * Unread is the server's: opening or receiving into the conversation on screen marks it read
 * through the API, and every change reloads the list and badge from there.
 */
@Component({
  selector: 'app-messages-drawer',
  templateUrl: './messages-drawer.component.html',
  styleUrls: ['./messages-drawer.component.scss'],
  standalone: false,
})
export class MessagesDrawerComponent implements OnInit, OnDestroy {
  isOpen = false;
  view: 'list' | 'thread' | 'new' = 'list';

  conversations: ConversationSummary[] = [];
  filter = '';

  thread: ConversationSummary | null = null;
  messages: ConversationMessage[] = [];
  hasMore = false;
  loadingThread = false;
  loadingOlder = false;
  draft = '';
  sending = false;
  editingUid: string | null = null;
  editDraft = '';
  confirmingDeleteUid: string | null = null;

  people: ConversationPerson[] = [];
  peopleSearch = '';
  picked: ConversationPerson[] = [];
  groupName = '';

  error: string | null = null;

  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;

  private readonly subs = new Subscription();
  private readonly peopleSearch$ = new Subject<string>();

  constructor(
    private messaging: MessagingService,
    private api: MessagingApiService,
    private router: Router,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subs.add(this.messaging.conversations$.subscribe((list) => (this.conversations = list)));
    this.subs.add(
      this.messaging.open$.subscribe((uid) => {
        this.isOpen = true;
        if (uid) {
          this.openThread(uid);
        } else {
          this.showList();
        }
      })
    );
    // A message into the conversation on screen: show it, and it is read. An event that does not
    // say which conversation it belongs to refreshes whichever thread is open.
    this.subs.add(
      this.messaging.live$.subscribe((event) => {
        const forThisThread = !event.conversationUid || event.conversationUid === this.thread?.uid;
        if (this.isOpen && this.view === 'thread' && this.thread && forThisThread) {
          this.refreshThread();
        }
      })
    );
    // A failed search shows its error and leaves the picker working for the next keystroke.
    this.subs.add(
      this.peopleSearch$
        .pipe(
          debounceTime(250),
          switchMap((search) =>
            this.api.people(search).pipe(
              catchError((err) => {
                this.error = this.messageOf(err);
                return of({ people: [] });
              })
            )
          )
        )
        .subscribe((result) => (this.people = result?.people ?? []))
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  open(): void {
    this.messaging.open(null);
    this.messaging.load();
  }

  close(): void {
    this.isOpen = false;
    this.thread = null;
    this.error = null;
  }

  /**
   * Escape closes the panel and goes no further: record screens treat a document-level Escape
   * as "leave this record", which must not fire from inside the panel.
   */
  onEscape(event: Event): void {
    event.stopPropagation();
    this.close();
  }

  /** Back to the list, with counts fresh from the server. */
  back(): void {
    this.showList();
    this.messaging.load();
  }

  showList(): void {
    this.view = 'list';
    this.thread = null;
    this.error = null;
  }

  get filteredConversations(): ConversationSummary[] {
    const f = this.filter.trim().toLowerCase();
    return f ? this.conversations.filter((c) => (c.title ?? '').toLowerCase().includes(f)) : this.conversations;
  }

  // --- one conversation -----------------------------------------------------

  openThread(uid: string): void {
    this.view = 'thread';
    this.loadingThread = true;
    this.error = null;
    this.messages = [];
    this.editingUid = null;
    this.confirmingDeleteUid = null;
    this.api.getMessages(uid).subscribe({
      next: (result) => {
        this.thread = result.conversation;
        this.messages = result.messages ?? [];
        this.hasMore = result.hasMore;
        this.loadingThread = false;
        this.scrollToBottom();
        this.markRead();
      },
      error: (err) => {
        this.loadingThread = false;
        this.error = this.messageOf(err);
      },
    });
  }

  /** Scrolling to the top of the thread pulls in the page before it. */
  onThreadScroll(): void {
    const el = this.scroller?.nativeElement;
    if (el && el.scrollTop < 40) {
      this.loadOlder();
    }
  }

  loadOlder(): void {
    if (!this.thread || !this.messages.length || !this.hasMore || this.loadingOlder) {
      return;
    }
    this.loadingOlder = true;
    const el = this.scroller?.nativeElement;
    const fromBottom = el ? el.scrollHeight - el.scrollTop : 0;
    this.api.getMessages(this.thread.uid, this.messages[0].id).subscribe({
      next: (result) => {
        this.messages = [...(result.messages ?? []), ...this.messages];
        this.hasMore = result.hasMore;
        this.loadingOlder = false;
        // Keep the reader where they were rather than jumping to the new top.
        setTimeout(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - fromBottom;
          }
        });
      },
      error: (err) => {
        this.loadingOlder = false;
        this.error = this.messageOf(err);
      },
    });
  }

  /** Re-read the newest page and merge it in, keeping the older pages already shown. */
  private refreshThread(): void {
    if (!this.thread) {
      return;
    }
    const uid = this.thread.uid;
    this.api.getMessages(uid).subscribe({
      next: (result) => {
        if (this.thread?.uid !== uid) {
          return;
        }
        const byId = new Map(this.messages.map((m) => [m.id, m]));
        for (const m of result.messages ?? []) {
          byId.set(m.id, m);
        }
        this.messages = Array.from(byId.values()).sort((a, b) => a.id - b.id);
        this.thread = { ...result.conversation };
        this.scrollToBottom();
        this.markRead();
      },
    });
  }

  private markRead(): void {
    const last = this.messages[this.messages.length - 1];
    // A reader of a document thread who has not posted keeps no marker.
    if (!this.thread?.isMember || !last) {
      this.messaging.load();
      return;
    }
    this.api.markRead(this.thread.uid, last.id).subscribe({
      next: () => this.messaging.load(),
      error: () => this.messaging.load(),
    });
  }

  send(): void {
    const body = this.draft.trim();
    if (!body || !this.thread || this.sending) {
      return;
    }
    this.sending = true;
    this.error = null;
    this.api.send(this.thread.uid, body).subscribe({
      next: () => {
        this.draft = '';
        this.sending = false;
        if (this.thread) {
          // Posting in a document thread makes you a participant.
          this.thread = { ...this.thread, isMember: true };
        }
        this.refreshThread();
      },
      error: (err) => {
        this.sending = false;
        this.error = this.messageOf(err);
      },
    });
  }

  /** Enter sends; Shift+Enter is a new line. */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  startEdit(message: ConversationMessage): void {
    this.editingUid = message.uid;
    this.editDraft = message.body;
    this.confirmingDeleteUid = null;
  }

  cancelEdit(): void {
    this.editingUid = null;
    this.editDraft = '';
  }

  saveEdit(message: ConversationMessage): void {
    const body = this.editDraft.trim();
    if (!body) {
      return;
    }
    this.api.edit(message.uid, body).subscribe({
      next: () => {
        this.cancelEdit();
        this.refreshThread();
      },
      error: (err) => (this.error = this.messageOf(err)),
    });
  }

  askDelete(message: ConversationMessage): void {
    this.confirmingDeleteUid = message.uid;
    this.editingUid = null;
  }

  confirmDelete(message: ConversationMessage): void {
    this.api.delete(message.uid).subscribe({
      next: () => {
        this.confirmingDeleteUid = null;
        this.refreshThread();
      },
      error: (err) => (this.error = this.messageOf(err)),
    });
  }

  leaveGroup(): void {
    if (!this.thread) {
      return;
    }
    this.api.leave(this.thread.uid).subscribe({
      next: () => {
        this.messaging.load();
        this.showList();
      },
      error: (err) => (this.error = this.messageOf(err)),
    });
  }

  /**
   * Where the open thread's record lives in the shell, or null when it is not a document thread
   * or its document type is one this shell has no screen for.
   */
  get recordUrl(): string | null {
    return documentViewUrl(this.thread?.documentType, this.thread?.documentUid);
  }

  /** The record a document thread is about. A print plate opens inside the print-plates addon. */
  openRecord(): void {
    const url = this.recordUrl;
    if (!url) {
      return;
    }
    this.router.navigateByUrl(url);
    this.close();
  }

  // --- a new conversation ---------------------------------------------------

  newMessage(): void {
    this.view = 'new';
    this.error = null;
    this.picked = [];
    this.groupName = '';
    this.peopleSearch = '';
    this.peopleSearch$.next('');
  }

  onPeopleSearch(value: string): void {
    this.peopleSearch = value;
    this.peopleSearch$.next(value);
  }

  isPicked(person: ConversationPerson): boolean {
    return this.picked.some((p) => p.uid === person.uid);
  }

  togglePick(person: ConversationPerson): void {
    this.picked = this.isPicked(person)
      ? this.picked.filter((p) => p.uid !== person.uid)
      : [...this.picked, person];
  }

  /** One person opens (or reopens) the direct conversation; several make a named group. */
  startConversation(): void {
    this.error = null;
    if (!this.picked.length) {
      this.error = this.translate.instant('messagingPickSomeone');
      return;
    }
    if (this.picked.length > 1 && !this.groupName.trim()) {
      this.error = this.translate.instant('messagingGroupNameRequired');
      return;
    }
    const request =
      this.picked.length === 1
        ? this.api.startDirect(this.picked[0].uid)
        : this.api.createGroup(this.groupName.trim(), this.picked.map((p) => p.uid));
    request.subscribe({
      next: (result) => {
        this.messaging.load();
        this.openThread(result.conversationUid);
      },
      error: (err) => (this.error = this.messageOf(err)),
    });
  }

  // --- helpers --------------------------------------------------------------

  /** Today shows the time; any other day shows the date. */
  timeLabel(iso: string): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    const now = new Date();
    return date.toDateString() === now.toDateString()
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { day: '2-digit', month: 'short' });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  /** The API's own words for a 400, a plain message for anything else. */
  private messageOf(err: any): string {
    const errors = err?.error?.errors;
    if (errors && typeof errors === 'object') {
      const text = Object.values(errors).flat().join(' ');
      if (text) {
        return text;
      }
    }
    return this.translate.instant('messagingSomethingWentWrong');
  }
}
